import React, { useState } from 'react';
import Select from 'react-select';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import useFilterOptions from './useFilterOptions';
import { selectStyles, selectTheme } from '../theme/selectTheme';

const STANDARD = ['blocks', 'gramPanchayats', 'villages', 'enumerators', 'dates'];

// Consolidated filter bar. Replaces the block/GP/village/enumerator/date filter
// UI + cascading + apply/clear that was copy-pasted across 5 files.
//
// Props:
//   fields        which standard fields to show (default: all)
//   extraFilters  [{ name, label, options, isMulti, isClearable, defaultValue }]
//   onApply(values)  values = { blocks[], gramPanchayats[], villages[], enumerators[],
//                               startDate, endDate, ...extras }
//   onClear()
//   loading
//   apiBase       optional — overrides which API the filter dropdowns load
//                 from (see useFilterOptions); defaults to the Express backend
export default function FilterBar({
  fields = STANDARD,
  extraFilters = [],
  onApply,
  onClear,
  loading = false,
  apiBase,
}) {
  const {
    options,
    cascadingGPs,
    cascadingVillages,
    loadingCascading,
    fetchGPsByBlocks,
    fetchVillagesByFilters,
    resetCascading,
    setCascadingVillages,
  } = useFilterOptions(apiBase);

  const show = (f) => fields.includes(f);

  const emptyExtras = () => {
    const o = {};
    extraFilters.forEach((ef) => {
      o[ef.name] = ef.isMulti ? [] : ef.defaultValue ?? null;
    });
    return o;
  };

  const [temp, setTemp] = useState({
    blocks: [],
    gramPanchayats: [],
    villages: [],
    enumerators: [],
    startDate: '',
    endDate: '',
    extras: emptyExtras(),
  });

  const handleBlockChange = (opts) => {
    const blocks = opts || [];
    setTemp((p) => ({ ...p, blocks, gramPanchayats: [], villages: [] }));
    fetchGPsByBlocks(blocks);
    setCascadingVillages([]);
  };
  const handleGPChange = (opts) => {
    const gps = opts || [];
    setTemp((p) => ({ ...p, gramPanchayats: gps, villages: [] }));
    fetchVillagesByFilters(temp.blocks, gps);
  };
  const handleVillageChange = (opts) => setTemp((p) => ({ ...p, villages: opts || [] }));
  const handleEnumChange = (opts) => setTemp((p) => ({ ...p, enumerators: opts || [] }));
  const handleDate = (e) => setTemp((p) => ({ ...p, [e.target.name]: e.target.value }));
  const handleExtra = (name, isMulti) => (opt) =>
    setTemp((p) => ({
      ...p,
      extras: { ...p.extras, [name]: isMulti ? opt || [] : opt ? opt.value : null },
    }));

  const buildValues = () => {
    const values = {
      blocks: temp.blocks.map((o) => o.value),
      gramPanchayats: temp.gramPanchayats.map((o) => o.value),
      villages: temp.villages.map((o) => o.value),
      enumerators: temp.enumerators.map((o) => o.value),
      startDate: temp.startDate,
      endDate: temp.endDate,
    };
    extraFilters.forEach((ef) => {
      const v = temp.extras[ef.name];
      values[ef.name] = ef.isMulti ? (v || []).map((o) => o.value) : v;
    });
    return values;
  };

  const handleApply = () => onApply && onApply(buildValues());

  const handleClear = () => {
    setTemp({
      blocks: [],
      gramPanchayats: [],
      villages: [],
      enumerators: [],
      startDate: '',
      endDate: '',
      extras: emptyExtras(),
    });
    resetCascading();
    if (onClear) onClear();
  };

  const extraValue = (ef) => {
    const v = temp.extras[ef.name];
    if (ef.isMulti) return v;
    return ef.options.find((o) => o.value === v) || null;
  };

  return (
    <div className="filterbar">
      <div className="filterbar-head">
        <FilterListIcon fontSize="small" /> Filter Data
      </div>
      <div className="filter-grid">
        {show('enumerators') && (
          <div className="filter-group">
            <label>Enumerator</label>
            <Select
              isMulti
              options={options.enumerators}
              value={temp.enumerators}
              onChange={handleEnumChange}
              placeholder="All enumerators"
              styles={selectStyles}
              theme={selectTheme}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              isClearable
            />
          </div>
        )}
        {show('blocks') && (
          <div className="filter-group">
            <label>Block</label>
            <Select
              isMulti
              options={options.blocks}
              value={temp.blocks}
              onChange={handleBlockChange}
              placeholder="All blocks"
              styles={selectStyles}
              theme={selectTheme}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              isClearable
              isLoading={loadingCascading}
            />
          </div>
        )}
        {show('gramPanchayats') && (
          <div className="filter-group">
            <label>Gram Panchayat</label>
            <Select
              isMulti
              options={cascadingGPs}
              value={temp.gramPanchayats}
              onChange={handleGPChange}
              placeholder="All gram panchayats"
              styles={selectStyles}
              theme={selectTheme}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              isClearable
              isLoading={loadingCascading}
            />
          </div>
        )}
        {show('villages') && (
          <div className="filter-group">
            <label>Village</label>
            <Select
              isMulti
              options={cascadingVillages}
              value={temp.villages}
              onChange={handleVillageChange}
              placeholder="All villages"
              styles={selectStyles}
              theme={selectTheme}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              isClearable
              isLoading={loadingCascading}
            />
          </div>
        )}
        {show('dates') && (
          <>
            <div className="filter-group">
              <label>Start Date</label>
              <input type="date" name="startDate" value={temp.startDate} onChange={handleDate} />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input type="date" name="endDate" value={temp.endDate} onChange={handleDate} />
            </div>
          </>
        )}
        {extraFilters.map((ef) => (
          <div className="filter-group" key={ef.name}>
            <label>{ef.label}</label>
            <Select
              isMulti={ef.isMulti}
              options={ef.options}
              value={extraValue(ef)}
              onChange={handleExtra(ef.name, ef.isMulti)}
              placeholder={ef.placeholder || `Select ${ef.label}`}
              styles={selectStyles}
              theme={selectTheme}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              isClearable={ef.isClearable ?? false}
            />
          </div>
        ))}
      </div>
      <div className="filter-actions">
        <button className="btn btn-primary" onClick={handleApply} disabled={loading}>
          <SearchIcon fontSize="small" /> {loading ? 'Applying…' : 'Apply Filters'}
        </button>
        <button className="btn btn-secondary" onClick={handleClear} disabled={loading}>
          <ClearIcon fontSize="small" /> Clear
        </button>
      </div>
    </div>
  );
}
